/* eslint-disable jira/integration/test-id-by-folder-structure */

/** @jsx jsx */
import React, { useCallback } from 'react';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form from '@atlaskit/form/form';
import { FormFooter } from '@atlaskit/form/form-footer';
import { FormSection } from '@atlaskit/form/form-section';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import type { ValueType } from '@atlaskit/select/types';
import SectionMessage from '@atlaskit/section-message/section-message';
import { useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkRemoteOAuth2ApplicationLink,
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
} from './CommonApplicationLinkCreationEntity.tsx';
import type { SelectOption } from './StageProps.types.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from './DisplayUrl.tsx';
import { putOAuth2ConfigInfo, updateApplinkV3 } from './fetchApplinkManifestForUrl.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from './applinksListPageUrl.constants.tsx';
import { formatBackendError } from './formatBackendError.tsx';
import CredentialsForm from './CredentialsForm.tsx';

const linkDetailsSectionHeadingMessage = defineMessage({
	defaultMessage: 'Link details',
	id: 'applinks.administration.ui.edit.link-details-section-heading',
	description: 'Heading for the section containing application link details on the edit page.',
});

const formCancelButtonTextMessage = defineMessage({
	defaultMessage: 'Cancel',
	id: 'applinks.administration.ui.edit.form-cancel-button-text',
	description: 'Label for button which discards the changes made on the edit page.',
});

const formSaveButtonTextMessage = defineMessage({
	defaultMessage: 'Save',
	id: 'applinks.administration.ui.edit.form-save-button-text',
	description: 'Label for button which saves the changes made on the edit page.',
});

const continueLinkingTitleMessage = defineMessage({
	defaultMessage: 'Continue linking to the external application',
	id: 'applinks.administration.ui.edit.continue-linking.title',
	description:
		'Title for the section prompting the user to continue linking to the external application.',
});

const continueLinkingDescriptionMessage = defineMessage({
	defaultMessage:
		'In order to complete the link, enter the credentials for the incoming and outgoing links below.',
	id: 'applinks.administration.ui.edit.continue-linking.description',
	description:
		'Description for the section prompting the user to continue linking to the external application.',
});

const oauth2ConfigLoadFailedTitleMessage = defineMessage({
	defaultMessage: 'Failed to load the OAuth 2.0 configuration',
	id: 'applinks.administration.ui.edit.oauth2-config-load-failed-title',
	description: 'Title for the error message displayed when OAuth 2.0 configuration fails to load.',
});

const oauth2ConfigLoadFailedDescriptionMessage = defineMessage({
	defaultMessage:
		"We couldn't load the OAuth 2.0 configuration for this app. This may be because the target app is inaccessible.",
	id: 'applinks.administration.ui.edit.oauth2-config-load-failed-description',
	description:
		'Description for the error message displayed when OAuth 2.0 configuration fails to load.',
});

const unknownErrorTextMessage = defineMessage({
	defaultMessage: 'We encountered an unknown error.',
	id: 'applinks.administration.ui.unknown-error',
	description:
		'A generic error message for when something goes wrong, but without any further detail.',
});

const styles = cssMap({
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
});

type OAuth2EditForm = {
	displayName: string;
	displayUrl: string;
	remoteApplicationSupportedScopes: ValueType<SelectOption, true>;
	applicationClientId?: string;
	applicationClientSecret?: string;
	redirectUrl?: string;
};

type ApplinksOAuth2EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo | null;
	partiallyLinkedMode?: boolean;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
};

const ApplinksOAuth2EditPageContents = (
	props: ApplinksOAuth2EditPageContentsProps,
): JSX.Element => {
	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = useCallback(
		async (data: OAuth2EditForm) => {
			try {
				const applinkUpdate: ApplicationLinkV3Simple = {
					id: props.applink.id,
					type: props.applink.type,
					system: props.applink.system,
					primary: props.applink.primary,
					isExtraAuthRequired: props.applink.isExtraAuthRequired,
					rpcUrl: props.applink.rpcUrl,

					// Only change these properties:
					name: data.displayName,
					displayUrl: data.displayUrl,
				};

				await updateApplinkV3(props.applink.id, applinkUpdate);

				if (props.oauth2ConfigInfo !== null) {
					const scopes = data.remoteApplicationSupportedScopes
						? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
						: [];
					const hasChanges = fg('cloud-connector-applinks-migration-m1-jira-srs')
						? data.applicationClientId ||
							data.applicationClientSecret ||
							data.redirectUrl ||
							scopes.length > 0 // only include scope checkes if feature gate is enabled
						: data.applicationClientId || data.applicationClientSecret || data.redirectUrl;
					if (hasChanges) {
						const oauth2ConfigUpdate: ApplicationLinkRemoteOAuth2ApplicationLink = {
							remoteClientId: data.applicationClientId,
							remoteClientSecret: data.applicationClientSecret,
							remoteRedirectUrl: data.redirectUrl,
							outboundScopes: scopes,
						};
						await putOAuth2ConfigInfo(props.applink.id, oauth2ConfigUpdate);
					}
				}
				push(getListPageUrl());
			} catch (err) {
				const maybeError = err instanceof Error ? err : undefined;
				// If you see this error:
				// - partially linked variant = outgoing credentials are inline
				// - normal variant = outgoing credentials are submitted via a separate modal (outgoing-credentials-modal)
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.oauth2.submitEditForm',
					`Failed to update OAuth 2.0 applink ${props.partiallyLinkedMode ? 'using partially linked variant' : 'using normal variant'} of the edit form`,
					maybeError,
				);
				if (fg('applinks-1093-redirect-error-fix')) {
					props.onDisplayError(
						formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)),
					);
				} else {
					props.onDisplayError(
						maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage),
					);
				}
				return err;
			}
		},
		[formatMessage, getListPageUrl, props, push],
	);

	return (
		<>
			<Form<OAuth2EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title={formatMessage(linkDetailsSectionHeadingMessage)}>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl name="rpcUrl" defaultUrl={props.applink.rpcUrl} disabled />
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							{props.partiallyLinkedMode && (
								<SectionMessage title={formatMessage(continueLinkingTitleMessage)}>
									<Text>{formatMessage(continueLinkingDescriptionMessage)}</Text>
								</SectionMessage>
							)}

							{props.oauth2ConfigInfo !== null && (
								<CredentialsForm
									{...props}
									oauth2ConfigInfo={props.oauth2ConfigInfo}
									onRequestStatusRefresh={props.onRequestStatusRefresh}
								/>
							)}
							{!props.oauth2ConfigInfo && (
								<SectionMessage
									appearance="error"
									title={formatMessage(oauth2ConfigLoadFailedTitleMessage)}
								>
									{formatMessage(oauth2ConfigLoadFailedDescriptionMessage)}
								</SectionMessage>
							)}

							<FormFooter>
								<ButtonGroup>
									<LinkButton href={getListPageUrl()} appearance="subtle">
										{formatMessage(formCancelButtonTextMessage)}
									</LinkButton>
									<Button
										testId="applinks-administration.ui.edit.oauth2.submit-button"
										type="submit"
										appearance="primary"
										isLoading={submitting}
									>
										{formatMessage(formSaveButtonTextMessage)}
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth2EditPageContents;
